import React, { useState } from "react"
import { useQueryClient } from "react-query"
import {
  Box, HStack, Input, InputGroup, InputLeftAddon, InputRightElement, Stack, Text,
} from "@chakra-ui/react"
import * as Yup from "yup"
import * as api from "api/events"
import { setCurrentEvent } from "context/actions"
import { useCurrentUser } from "queries/users"
import { useForms } from "queries/forms"
import { DioboxEvent, EventEntryTarget, FormType } from "sharedTypes"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import Select from "components/ReactHookForm/Select"
import Button from "components/Buttons/Button"
import Pill from "components/elements/Pill"
import UpgradeAlert from "components/elements/UpgradeAlert"
import { successToast } from "utilities/toasts"
import { automaticTarget, formNote, webPageNote } from "components/EventLink/destinations"
import RadioCards from "components/ReactHookForm/RadioCards"
import ChangeEventLink from "./ChangeEventLink"
import Destination from "./Destination"
import { LinkActions, LinkPrefixText, linkPrefixStyle } from "./LinkParts"

type Props = {
  event: DioboxEvent
  onHide: () => void
}

type Values = {
  rootTarget: EventEntryTarget
  rootFormId: string
}

const validationSchema = Yup.object().shape({
  rootFormId: Yup.string().when("rootTarget", {
    is: "form",
    then: (schema) => schema.required("Please select a form"),
  }),
})

const EventLink = ({ event, onHide }: Props) => {
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const [changing, setChanging] = useState(false)
  const { data } = useForms(event.id)
  const forms = (data?.data ?? [])
    .filter(({ type }) => ![FormType.RSVP, FormType.Survey].includes(type))
  const editable = event.vanityUrlsEnabled
  const rootUrl = event.urls?.root

  const onSubmit = (values: Values) => api.putEventEntry(event.id, {
    rootTarget: values.rootTarget,
    rootFormId: values.rootTarget === "form" ? Number(values.rootFormId) : null,
  }).then(({ data: updated }) => {
    if (currentUser) setCurrentEvent(queryClient, updated, currentUser)
    successToast({ title: "Destination updated" })
    onHide()
  }).catch(() => {})

  const initialValues: Values = {
    rootTarget: event.rootTarget ?? automaticTarget(event),
    rootFormId: event.rootFormId ? String(event.rootFormId) : "",
  }

  return (
    <>
      <ModalForm
        key={JSON.stringify(initialValues)}
        title="Event Link Settings"
        onClose={onHide}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        disableSubmitWhenNotDirty
        size="2xl"
      >
        {({ control, watch, setValue }) => {
          const selectedForm = forms.find(({ id }) => String(id) === watch("rootFormId"))
          const formUrl = selectedForm?.url

          return (
            <Stack spacing={7}>
              <Text>
                Set a short, custom link for the event. It’s used for the Web Page,
                Event Portal, forms, and guest links.
              </Text>

              <Box>
                <InputGroup size="lg">
                  <InputLeftAddon {...linkPrefixStyle}>
                    <LinkPrefixText url={rootUrl} />
                  </InputLeftAddon>
                  <Input
                    value={event.slug ?? ""}
                    isReadOnly
                    aria-label="Current Event Link"
                    fontFamily="mono"
                    fontSize="sm"
                    fontWeight="semibold"
                    bg="gray.50"
                    _readOnly={{ color: "gray.500" }}
                    pr={40}
                  />
                  <InputRightElement w="auto" pr={2}>
                    <HStack spacing={1}>
                      {rootUrl && <LinkActions url={rootUrl} />}
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={editable ? undefined : "lock"}
                        isDisabled={!editable}
                        onClick={() => setChanging(true)}
                      >
                        Edit
                      </Button>
                    </HStack>
                  </InputRightElement>
                </InputGroup>
                {!editable && (
                  <Box mt={2}>
                    <UpgradeAlert text="Custom Event Links are available on paid plans." />
                  </Box>
                )}
              </Box>

              <Box>
                <Text fontSize="xl" fontWeight="semibold">Destination</Text>
                <Text mt={1} mb={4}>Set where the Event Link goes.</Text>
                <RadioCards name="rootTarget" control={control}>
                  <Destination
                    value="portal"
                    icon="guest-portal"
                    title="Event Portal"
                    description="A listing of all public forms, with your event branding."
                    slug={event.slug}
                    url={event.urls?.portal}
                  />
                  <Destination
                    value="form"
                    icon="forms"
                    title="Form"
                    description="Any published event form."
                    slug={event.slug}
                    url={formUrl}
                    note={formNote(selectedForm, event)}
                  >
                    <Box mt={3} maxW="280px">
                      <Select
                        name="rootFormId"
                        control={control}
                        size="sm"
                        mb={0}
                        includeBlank
                        blankLabel="Select a form"
                        onChange={({ target: { value } }) => value && setValue("rootTarget", "form", { shouldDirty: true })}
                        options={forms.map(({ name, id }) => ({
                          label: name, value: String(id),
                        }))}
                      />
                    </Box>
                  </Destination>
                  <Destination
                    value="site"
                    icon="website"
                    title="Web Page"
                    description={event.website?.published
                      ? "The published event web page."
                      : "The event web page."}
                    note={webPageNote(event)}
                    slug={event.slug}
                    url={event.urls?.site}
                    badge={event.website && (
                      <Pill
                        label={event.website.published ? "Published" : "Draft"}
                        color={event.website.published ? "blue.400" : "gray.400"}
                        width="auto"
                        px={2}
                        h="20px"
                      />
                    )}
                  />
                  <Destination
                    value="none"
                    icon="cancel-circle"
                    title="Disabled"
                    description={"An “Event not found” page appears. Other pages remain "
                      + "available from their own direct links."}
                  />
                </RadioCards>
              </Box>
            </Stack>
          )
        }}
      </ModalForm>
      {changing && <ChangeEventLink event={event} onHide={() => setChanging(false)} />}
    </>
  )
}

export default EventLink
